import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Icon, Textarea } from "@/ds";
import { anonymousKeyFor, executeAction, rejectAction } from "@/lib/dany";

/**
 * "Ask this FixyBee to quote my job", as a confirmation (DY2.1).
 *
 * Same contract as the job draft: Dany proposes with `request_quote`, the
 * `action_id` in `tool_result.data` is the only thing that can execute it, and
 * the button below is where a person decides. No new entity is created — sending
 * this notifies one FixyBee about a job they can already see, so what the client
 * is committing to is an invitation, not a contract. The copy says exactly that,
 * including that the FixyBee may decline.
 */
export default function DanyQuoteRequestWidget({ data, threadId, onCommitted }) {
  const { t } = useTranslation();
  const [note, setNote] = React.useState(data?.request?.note || "");
  const [busy, setBusy] = React.useState(false);
  const [phase, setPhase] = React.useState("draft");
  const [error, setError] = React.useState(null);

  const actionId = data?.action_id;
  const request = data?.request || {};
  const name = request.provider_name || "";

  const send = async () => {
    if (!actionId) return;
    setBusy(true);
    setError(null);
    try {
      await executeAction(actionId, { ...request, note }, {
        anonymousKey: anonymousKeyFor(threadId),
      });
      setPhase("sent");
      // The thread re-reads itself: this card is not the only thing on
      // screen that this press changed (T11 N38).
      onCommitted?.();
    } catch (err) {
      setError(
        t([`dany.quoteRequest.${err.code}`, `dany.errors.${err.code}`,
           "dany.quoteRequest.failed"], {
          defaultValue: t("dany.quoteRequest.failed"),
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    setBusy(true);
    try {
      await rejectAction(actionId, { anonymousKey: anonymousKeyFor(threadId) });
    } catch { /* the card goes away either way */ }
    setPhase("discarded");
    // The thread re-reads itself: this card is not the only thing on
    // screen that this press changed (T11 N38).
    onCommitted?.();
    setBusy(false);
  };

  if (phase === "discarded") return null;

  if (phase === "sent") {
    return (
      <Card variant="raised" padding="md" data-testid="dany-quote-request-sent">
        <p style={heading}>{t("dany.quoteRequest.sentTitle", { name })}</p>
        <p style={body}>{t("dany.quoteRequest.sentBody")}</p>
      </Card>
    );
  }

  return (
    <Card variant="raised" padding="md" data-testid="dany-quote-request">
      <span style={eyebrow}>
        <Icon name="file-text" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.quoteRequest.header", { name })}
      </span>
      <p style={body}>{t("dany.quoteRequest.body", { name })}</p>
      {request.job_title && (
        <p style={{
          marginTop: "var(--space-3)", fontFamily: "var(--font-display)",
          fontWeight: "var(--weight-bold)", fontSize: "var(--text-base)",
          color: "var(--text-body)",
        }}>{request.job_title}</p>
      )}
      <label style={{ display: "block", marginTop: "var(--space-3)" }}>
        <span style={{
          display: "block", marginBottom: 4, fontFamily: "var(--font-display)",
          fontSize: "var(--text-xs)", textTransform: "uppercase",
          letterSpacing: "var(--tracking-widest)", color: "var(--dany-meta)",
        }}>{t("dany.quoteRequest.note")}</span>
        <Textarea rows={3} value={note} maxLength={280}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="dany-quote-request-note" />
      </label>
      {error && (
        <p style={{ ...body, color: "var(--danger)" }}
           data-testid="dany-quote-request-error">{error}</p>
      )}
      <div style={{
        marginTop: "var(--space-4)", display: "flex", gap: "var(--space-2)",
        flexWrap: "wrap",
      }}>
        <Button variant="primary" size="sm" iconRight="arrow-right"
                disabled={busy || !actionId} onClick={send}
                data-testid="dany-quote-request-send">
          {t("dany.quoteRequest.send")}
        </Button>
        <Button variant="ghost" size="sm" icon="x" disabled={busy}
                onClick={discard}>{t("dany.job.discard")}</Button>
      </div>
    </Card>
  );
}

const eyebrow = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const heading = {
  fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
  fontSize: "var(--text-lg)", color: "var(--text-body)",
};

const body = {
  marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
  color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
  maxWidth: "var(--measure-body)",
};
